// Round 11 — the expanded Step 3 "Health History" condition grid, requested by
// program medical staff. ONE shared config drives the form rows, validation,
// Step 6 review, the application.pdf renderer, and the tracker mapping, so the
// list stays maintainable in one place and identical for both programs.
//
// Every row is a required Yes/No; on Yes an explanation is required. Rows may
// additionally declare structured extras that appear on Yes:
//   * extraText  — free text (optional)
//   * extraYesNo — Yes/No, REQUIRED when the row is Yes
//   * extraDate  — date, optional
// Each row uses at most one extra of each kind, so HealthConditionEntry holds
// one generic slot per kind (see types/index.ts).

import type { HealthConditionEntry, HealthConditionKey } from "./types";

export interface HealthConditionSpec {
  id: HealthConditionKey;
  /** Full row label shown on the Step 3 grid and PDF/review Yes rows. */
  label: string;
  /** Compact label for error messages, tracker cells, and "No" summaries. */
  shortLabel: string;
  /** Label of the explanation field that appears (required) on Yes. */
  explanationLabel: string;
  extraText?: { label: string };
  extraYesNo?: { label: string };
  extraDate?: { label: string };
}

export const HEALTH_CONDITIONS: readonly HealthConditionSpec[] = [
  {
    id: "diabetes",
    label: "Diabetes",
    shortLabel: "Diabetes",
    explanationLabel: "Please explain",
    extraText: { label: "Last HbA1c percentage and date" },
    extraYesNo: { label: "Insulin pump" },
  },
  {
    id: "hypertension",
    label: "Hypertension (high blood pressure)",
    shortLabel: "Hypertension",
    explanationLabel: "Please explain",
  },
  {
    id: "heartDisease",
    label:
      "Adult or congenital heart disease / heart attack / chest pain (angina) / heart murmur / coronary artery disease; any heart surgery or procedure",
    shortLabel: "Heart disease",
    explanationLabel: "Please explain",
  },
  {
    id: "familyHeartHistory",
    label:
      "Family history of heart disease or any sudden heart-related death of a family member before age 50",
    shortLabel: "Family heart history",
    explanationLabel: "Please explain",
  },
  {
    id: "strokeTia",
    label: "Stroke/TIA",
    shortLabel: "Stroke/TIA",
    explanationLabel: "Please explain",
  },
  {
    id: "asthma",
    label: "Asthma / reactive airway disease",
    shortLabel: "Asthma",
    explanationLabel: "Please explain",
    extraDate: { label: "Last attack date" },
  },
  {
    id: "lungDisease",
    label: "Lung / respiratory disease",
    shortLabel: "Lung/respiratory disease",
    explanationLabel: "Please explain",
  },
  {
    id: "copd",
    label: "COPD",
    shortLabel: "COPD",
    explanationLabel: "Please explain",
  },
  {
    id: "earEyesNoseSinus",
    label: "Ear / eyes / nose / sinus problems",
    shortLabel: "Ear/eyes/nose/sinus",
    explanationLabel: "Please explain",
  },
  {
    id: "musculoskeletal",
    label: "Muscular / skeletal condition / muscle or bone issues",
    shortLabel: "Muscular/skeletal",
    explanationLabel: "Please explain",
  },
  {
    id: "headInjury",
    label: "Head injury / concussion / TBI",
    shortLabel: "Head injury/concussion",
    explanationLabel: "Please explain",
  },
  {
    id: "altitudeSickness",
    label: "Altitude sickness",
    shortLabel: "Altitude sickness",
    explanationLabel: "Please explain",
  },
  {
    id: "psychiatric",
    label: "Psychiatric / psychological or emotional difficulties",
    shortLabel: "Psychiatric/emotional",
    explanationLabel: "Please explain",
  },
  {
    id: "neurological",
    label: "Neurological / behavioral disorders",
    shortLabel: "Neurological/behavioral",
    explanationLabel: "Please explain",
  },
  {
    id: "bloodDisorders",
    label: "Blood disorders / sickle cell disease",
    shortLabel: "Blood disorders",
    explanationLabel: "Please explain",
  },
  {
    id: "fainting",
    label: "Fainting spells and dizziness",
    shortLabel: "Fainting/dizziness",
    explanationLabel: "Please explain",
  },
  {
    id: "kidneyDisease",
    label: "Kidney disease",
    shortLabel: "Kidney disease",
    explanationLabel: "Please explain",
  },
  {
    id: "seizures",
    label: "Seizures or epilepsy",
    shortLabel: "Seizures/epilepsy",
    explanationLabel: "Please explain",
    extraDate: { label: "Last seizure date" },
  },
  {
    id: "abdominal",
    label: "Abdominal / stomach / digestive problems",
    shortLabel: "Abdominal/digestive",
    explanationLabel: "Please explain",
  },
  {
    id: "thyroid",
    label: "Thyroid disease",
    shortLabel: "Thyroid disease",
    explanationLabel: "Please explain",
  },
  {
    id: "skin",
    label: "Skin issues",
    shortLabel: "Skin issues",
    explanationLabel: "Please explain",
  },
  {
    id: "sleepApnea",
    label: "Obstructive sleep apnea / sleep disorders",
    shortLabel: "Sleep apnea/sleep disorders",
    explanationLabel: "Please explain",
    extraYesNo: { label: "CPAP" },
  },
  {
    id: "surgeries",
    label: "Surgeries and hospitalizations",
    shortLabel: "Surgeries/hospitalizations",
    // The explanation field IS the list itself.
    explanationLabel: "List all surgeries and hospitalizations",
    extraDate: { label: "Last surgery date" },
  },
  {
    id: "otherConditions",
    label: "Any other medical conditions not covered above",
    shortLabel: "Other conditions",
    // The explanation field IS the list itself.
    explanationLabel: "List any other medical conditions",
  },
];

export function emptyHealthConditionEntry(): HealthConditionEntry {
  return { answer: null, explanation: "", extraText: "", extraYesNo: null, extraDate: "" };
}

export function defaultHealthConditions(): Record<HealthConditionKey, HealthConditionEntry> {
  const out = {} as Record<HealthConditionKey, HealthConditionEntry>;
  for (const spec of HEALTH_CONDITIONS) out[spec.id] = emptyHealthConditionEntry();
  return out;
}

/** Rebuilds a stored entry to the current shape (drafts may predate fields). */
export function normalizeHealthConditionEntry(
  e: Partial<HealthConditionEntry> | undefined
): HealthConditionEntry {
  return {
    answer: e?.answer === "Yes" || e?.answer === "No" ? e.answer : null,
    explanation: typeof e?.explanation === "string" ? e.explanation : "",
    extraText: typeof e?.extraText === "string" ? e.extraText : "",
    extraYesNo: e?.extraYesNo === "Yes" || e?.extraYesNo === "No" ? e.extraYesNo : null,
    extraDate: typeof e?.extraDate === "string" ? e.extraDate : "",
  };
}

/**
 * One-line detail for a Yes row (explanation + any extras), shared by the
 * Step 6 review and the application.pdf renderer.
 */
export function healthConditionDetail(spec: HealthConditionSpec, e: HealthConditionEntry): string {
  const parts: string[] = [];
  if (e.explanation.trim()) parts.push(e.explanation.trim());
  if (spec.extraText && e.extraText.trim()) parts.push(`${spec.extraText.label}: ${e.extraText.trim()}`);
  if (spec.extraYesNo && e.extraYesNo) parts.push(`${spec.extraYesNo.label}: ${e.extraYesNo}`);
  if (spec.extraDate && e.extraDate.trim()) parts.push(`${spec.extraDate.label}: ${e.extraDate.trim()}`);
  return parts.join(" · ");
}
